/**
 * SelectionListUI - 基于 Bootstrap 的选择列表控制组件
 * 提供可动态添加/删除选项的选择列表组件，支持选中状态和onChange事件
 */

import { VisionaryObject } from '@/visionaryControllers/visionaryScene';
import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { getI18nText } from '@/utils/commonFunc';

export interface ListItemOption {
  /** 选项值 */
  value: string;
  /** 显示文本 */
  text: string;
  /** 图片 */
  img?: string;
  /** 是否隐藏 */
  hidden?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否选中 */
  selected?: boolean;
  /** 多语言键 */
  i18nKey?: string;
  /** 是否是 marble 模型 */
  isMarbleModel?: boolean;
  worldid?: string;
  modelUrl?: string;
  status?: string;
  isPlyHeatmap?: boolean; // 是否是经语义处理的ply模型文件
}

export interface SelectionListOptions extends ElementOptions {
  /** 标签文本 */
  label?: string;
  /** 是否显示添加按钮 */
  showAddBtn?: boolean;
  /** 是否显示标签 */
  showLabel?: boolean;
  /** 是否显示空列表 */
  showEmpty?: boolean;
  /** 初始选项列表 */
  selectItemList?: ListItemOption[];
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否多选 */
  multiple?: boolean;
  /** 列表项样式类 */
  listItemClass?: string;
  /** 列表容器样式类 */
  listContainerClass?: string;
  /** 是否显示悬浮按钮 */
  isShowHoverBtn?: boolean;
  /** 自定义容器样式类 */
  containerClass?: string;
  /** 多语言键 */
  i18nKey?: string;
}

export class SelectionListUI {
  private ui: BootstrapUI;
  private emptyListContainer: HTMLElement | null = null;
  private emptyAddButton: HTMLElement | null = null;
  private collapseButton: HTMLElement | null = null;
  private modelListContainer!: HTMLElement;
  private container: HTMLElement;
  private listContainer: HTMLElement;
  private isListCollapsed = false;
  private options: SelectionListOptions;
  private onChangeCallbacks: ((value: string | string[], option: ListItemOption | ListItemOption[]) => void)[] = [];
  private onShowCallbacks: ((option: ListItemOption) => void)[] = [];
  private onHideCallbacks: ((option: ListItemOption) => void)[] = [];
  private onCopyCallbacks: ((option: ListItemOption) => void)[] = [];
  private onDeleteCallbacks: ((option: ListItemOption) => void)[] = [];
  private onLoadCallbacks: ((option: ListItemOption) => void)[] = [];
  private onAddCallbacks: ((params: any) => void)[] = [];
  private onTextClickCallbacks: ((option: ListItemOption) => void)[] = [];
  private onRemoveCallbacks: ((option: ListItemOption) => void)[] = [];
  private selectItemList: ListItemOption[] = [];

  constructor(options: SelectionListOptions = {}) {
    this.ui = bootstrapUI;
    this.options = {
      label: '选择列表',
      showAddBtn: false,
      showLabel: true,
      showEmpty: false,
      disabled: false,
      multiple: false,
      listItemClass: 'selection-list-item',
      listContainerClass: 'selection-list-container',
      isShowHoverBtn: false,
      containerClass: '',
      ...options
    };

    this.selectItemList = [...(this.options.selectItemList || [])];
    this.container = this.createElement();
    this.listContainer = this.container.querySelector('.selection-list-container') as HTMLElement;

    this.setupEventHandlers();
    this.updateOptions();
  }

  /**
   * 创建选择列表组件元素
   * @returns 包含列表的容器元素
   */
  private createElement(): HTMLElement {
    let labeli18nKey = '';
    if (this.options.i18nKey) {
      labeli18nKey = this.options.i18nKey;
      this.options.i18nKey = '';
    }
    const container = this.ui.createElement({
      className: [
        'selection-list-ui-container',
        ...(this.options.containerClass ? [this.options.containerClass] : []),
      ],
      ...this.options
    });

    const modelListContainer = this.ui.createElement({
      tag: 'div',
      className: 'model-list-container',
    });
    if (this.options.showAddBtn || this.options.showLabel) {
      const titleElement = this.ui.createElement({
        tag: 'div',
        className: 'selection-list-ui-title',
      });
      const leftElement = this.ui.createElement({
        tag: 'div',
        className: ['display-flex','flex-gap-50'],
      });
      const collapseBtn = this.ui.createElement({
        tag: 'i',
        // className: ['icon', 'iconfont', 'icon-ExpandOutlined', 'selection-list-collapse-btn','no-border-btn'],
        className: ['icon', 'iconfont', 'icon-SmallArrowOutlined', 'selection-list-collapse-btn','no-border-btn'],
        attributes: {
          title: 'Collapse',
          role: 'button',
          tabindex: '0',
          'aria-label': 'Collapse'
        }
      });
      collapseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.toggleListCollapsed();
      });
      collapseBtn.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          this.toggleListCollapsed();
        }
      });
      this.collapseButton = collapseBtn;
      leftElement.appendChild(collapseBtn);
      titleElement.appendChild(leftElement);
      // 创建标签
      if (this.options.showLabel && this.options.label) {
        const label = this.ui.createElement({
          tag: 'label',
          className: 'selection-list-ui-label',
          textContent: this.options.label,
          attributes: {
            for: this.options.id || ''
          },
          i18nKey: labeli18nKey
        });
        leftElement.appendChild(label);
      }
      const actionContainer = this.ui.createElement({
        tag: 'div',
        className: 'selection-list-ui-actions',
      });
      if (this.options.showAddBtn) {
        const addBtn = this.ui.createElement({
          tag: 'i',
          className: ['icon', 'iconfont', 'icon-PlusOutlined', 'selection-list-action-btn'],
          attributes: {
            title: getI18nText('label.addModel'),
            'data-i18n-title': 'label.addModel'
          }
        });
        addBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.notifyAdd(addBtn);
        });
        actionContainer.appendChild(addBtn);
      }
      titleElement.appendChild(actionContainer);
      modelListContainer.appendChild(titleElement);
    }
    // 创建列表容器
    const listContainer = this.ui.createElement({
      className: [
        'selection-list-container',
        ...(this.options.listContainerClass ? [this.options.listContainerClass] : []),
      ],
      attributes: {
        ...(this.options.id && { id: this.options.id + '-container' }),
        ...(this.options.disabled && { 'aria-disabled': 'true' }),
        'aria-expanded': 'true'
      }
    });

    modelListContainer.appendChild(listContainer);
    this.modelListContainer = modelListContainer;
    container.appendChild(modelListContainer);

    // 创建空列表容器
    if (this.options.showEmpty) {
      const emptyElementContainer = this.ui.createElement({
        tag: 'div',
        className: 'selection-list-item-empty-container',
      });

      const emptyTextElement = this.ui.createElement({
        tag: 'div',
        className: 'empty-message',
      });
      let emptyText = getI18nText('label.empty');
      emptyTextElement.innerHTML = `<i class="icon iconfont icon-ModelCardOutlined"></i><div class="empty-text" data-i18n="label.empty">${emptyText}</div>`;
      emptyElementContainer.appendChild(emptyTextElement);
      const emptyButtonElement = this.ui.createButton({
        iconName: 'PlusOutlined',
        isIconOnly: true,
        className: 'empty-button',
      });

      let addModelText = getI18nText('label.addModel');
      emptyButtonElement.innerHTML = `<i class="icon iconfont icon-PlusOutlined"></i><span data-i18n="label.addModel">${addModelText}</span>`;
      emptyElementContainer.appendChild(emptyButtonElement);

      this.emptyAddButton = emptyButtonElement;
      this.emptyListContainer = emptyElementContainer;
      // container.appendChild(emptyElementContainer);
      modelListContainer.appendChild(emptyElementContainer);
    }

    return container;
  }

  private toggleListCollapsed(): void {
    this.isListCollapsed = !this.isListCollapsed;
    this.applyCollapseState();
  }

  private applyCollapseState(): void {
    if (this.isListCollapsed) {
      this.listContainer.classList.add('hidden');
      this.modelListContainer.classList.add('list-collapsed');
      this.listContainer.setAttribute('aria-expanded', 'false');
      if (this.collapseButton) {
        this.collapseButton.setAttribute('title', 'Expand');
        this.collapseButton.setAttribute('aria-label', 'Expand');
      }
      if(this.emptyListContainer) {
        this.emptyListContainer.classList.add('hidden');
      }
    } else {
      this.listContainer.classList.remove('hidden');
      this.modelListContainer.classList.remove('list-collapsed');
      this.listContainer.setAttribute('aria-expanded', 'true');
      if (this.collapseButton) {
        this.collapseButton.setAttribute('title', 'Collapse');
        this.collapseButton.setAttribute('aria-label', 'Collapse');
      }
      this.updateOptions();
    }
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    if (this.options.showEmpty && this.emptyAddButton) {
      this.emptyAddButton.addEventListener('click', () => {
        this.notifyAdd(this.emptyAddButton);
      });
    }
    // 列表项点击事件
    this.ui.on('click', this.listContainer, (e) => {
      const target = e.target as HTMLElement;

      // 检查是否点击了操作按钮
      if (target.classList.contains('selection-list-action-btn')) {
        e.stopPropagation();
        this.handleActionClick(target);
        return;
      }

      // 检查是否点击了文本区域
      if (target.classList.contains('selection-list-item-text') || target.closest('.selection-list-item-text')) {
        const textElement = target.classList.contains('selection-list-item-text')
          ? target
          : target.closest('.selection-list-item-text');
        const listItem = textElement?.closest('.selection-list-item');

        if (listItem && !this.options.disabled) {
          const value = listItem.getAttribute('data-value');
          if (value) {
            const option = this.selectItemList.find(opt => opt.value === value);
            if (option && !option.disabled) {
              e.stopPropagation();
              this.notifyTextClick(option);
              return;
            }
          }
        }
      }
    });
  }

  /**
   * 处理操作按钮点击
   */
  private handleActionClick(button: HTMLElement): void {
    const action = button.getAttribute('data-action');
    const value = button.getAttribute('data-value');

    if (!action || !value) {
      return;
    }

    const option = this.selectItemList.find(opt => opt.value === value);
    if (!option) {
      return;
    }

    switch (action) {
      case 'show': // 显示
        option.hidden = false;
        this.updateVisibilityButton(value, false);
        this.notifyShow(option);
        break;
      case 'hide':
        option.hidden = true;
        this.updateVisibilityButton(value, true);
        this.notifyHide(option);
        break;
      case 'copy': // 复制
        this.notifyCopy(option);
        break;
      case 'delete': // 删除
        this.notifyDelete(option);
        break;
      case 'load': // 加载
        this.notifyLoad(option);
        break;
      case 'remove': // 删除
        this.notifyRemove(option);
        break;
    }
  }

  /**
   * 更新显示/隐藏按钮的图标和状态
   */
  private updateVisibilityButton(value: string, hidden: boolean): void {
    const listItem = this.listContainer.querySelector(`[data-value="${value}"]`);
    if (!listItem) return;

    const visibilityBtn = listItem.querySelector('.selection-list-action-btn');
    if (!visibilityBtn) return;

    // 更新图标
    if (hidden) {
      visibilityBtn.classList.remove('icon-show-annotation');
      visibilityBtn.classList.add('icon-hidden-annotation');
      visibilityBtn.setAttribute('title', '显示');
      visibilityBtn.setAttribute('data-action', 'show');
      listItem.classList.add('hidden');
    } else {
      visibilityBtn.classList.remove('icon-hidden-annotation');
      visibilityBtn.classList.add('icon-show-annotation');
      visibilityBtn.setAttribute('title', '隐藏');
      visibilityBtn.setAttribute('data-action', 'hide');
      listItem.classList.remove('hidden');
    }
  }

  /**
   * 处理列表项点击
   */
  private handleItemClick(option: ListItemOption, listItem: HTMLElement): void {
    return;
    if (this.options.multiple) {
      // 多选模式
      option.selected = !option.selected;
      this.updateItemState(listItem, option);

      const selectedOptions = this.selectItemList.filter(opt => opt.selected);
      const selectedValues = selectedOptions.map(opt => opt.value);
      this.notifyChange(selectedValues, selectedOptions);
    } else {
      // 单选模式
      if (!option.selected) {
        // 清除其他选中项
        this.selectItemList.forEach(opt => {
          if (opt !== option) {
            opt.selected = false;
          }
        });
        option.selected = true;
        this.updateAllItemsState();
        this.notifyChange(option.value, option);
      }
    }
  }

  /**
   * 更新单个列表项状态
   */
  private updateItemState(listItem: HTMLElement, option: ListItemOption): void {
    if (option.selected) {
      listItem.classList.add('selected');
    } else {
      listItem.classList.remove('selected');
    }

    if (option.disabled) {
      listItem.classList.add('disabled');
    } else {
      listItem.classList.remove('disabled');
    }
  }

  /**
   * 更新所有列表项状态
   */
  private updateAllItemsState(): void {
    const listItems = this.listContainer.querySelectorAll('.selection-list-item');
    listItems.forEach((item) => {
      const value = item.getAttribute('data-value');
      if (value) {
        const option = this.selectItemList.find(opt => opt.value === value);
        if (option) {
          this.updateItemState(item as HTMLElement, option);
        }
      }
    });
  }

  /**
   * 更新选项列表
   */
  private updateOptions(): void {
    // 清空现有选项
    this.listContainer.innerHTML = '';

    if (this.selectItemList.length == 0) {
      // if(!this.options.showEmpty){
      //   this.modelListContainer.classList.add('hidden');
      // }
      if (this.options.showEmpty && this.emptyListContainer) {
        this.emptyListContainer.classList.remove('hidden');
      }
    } else {
      this.modelListContainer.classList.remove('hidden');
      if (this.options.showEmpty && this.emptyListContainer) {
        this.emptyListContainer.classList.add('hidden');
      }

      // 添加选项
      this.selectItemList.forEach(option => {
        const listItem = this.ui.createElement({
          className: this.options.listItemClass || 'selection-list-item',
          attributes: {
            'data-value': option.value,
            role: 'button',
            tabindex: '0',
            ...(option.disabled && { 'aria-disabled': 'true' })
          }
        });

        // 创建图片容器
        const imgContainer = this.ui.createElement({
          className: 'selection-list-item-img'
        });

        if(option.isPlyHeatmap) {
          imgContainer.style.display = 'block';
          option.img = new URL('/images/plyHeatmapIcon.svg', import.meta.url).href;
          console.log("经语义处理的ply模型文件", option.img);
        }

        if (option.img) {
          const img = this.ui.createElement({
            tag: 'img',
            attributes: {
              src: option.img,
              alt: option.text
            }
          });
          imgContainer.appendChild(img);
        }
        listItem.appendChild(imgContainer);

        // 创建文本容器
        const textSpan = this.ui.createElement({
          tag: 'span',
          className: 'selection-list-item-text',
          textContent: option.text,
          attributes: {
            title: option.text
          }
        });
        listItem.appendChild(textSpan);

        // 创建工具栏容器（始终显示）
        if (this.options.isShowHoverBtn) {
          const toolbar = this.ui.createElement({
            className: 'selection-list-item-toolbar'
          });
          if (option.isMarbleModel) {
            toolbar.classList.add('selection-list-item-toolbar-marble');
            if (option.status == 'generating' || option.status == 'create') { // 显示生成中标签
              const loadingBtn = this.ui.createElement({
                tag: 'label',
                className: ['selection-list-loading-label'],
                textContent: getI18nText('label.generating'),
                styles: {
                  margin: '0',
                  whiteSpace: 'nowrap'
                },
                i18nKey: 'label.generating'
              });
              toolbar.appendChild(loadingBtn);
            } else if (option.status == 'success') { // 显示load按钮
              const successBtn = this.ui.createElement({
                tag: 'label',
                className: ['selection-list-loading-label', 'load', 'selection-list-action-btn'],
                textContent: getI18nText('label.load'),
                attributes: {
                  title: '载入',
                  'data-action': 'load',
                  'data-value': option.value,
                  'data-worldid': option.worldid || '',
                  'data-modelurl': option.modelUrl || '',
                },
                i18nKey: 'label.load'
              });
              toolbar.appendChild(successBtn);
            } else if (option.status == 'failed') { // 显示删除按钮
              const failedBtn = this.ui.createElement({
                tag: 'label',
                className: ['selection-list-loading-label', 'failed', 'selection-list-action-btn'],
                textContent: getI18nText('label.failed'),
                attributes: {
                  title: '失败',
                  'data-action': 'failed',
                  'data-value': option.value,
                  'data-worldid': option.worldid || ''
                },
                i18nKey: 'label.failed'
              });
              toolbar.appendChild(failedBtn);
              // 删除按钮
              const removeBtn = this.ui.createElement({
                tag: 'i',
                className: ['icon', 'iconfont', 'icon-shanchu', 'selection-list-action-btn'],
                attributes: {
                  title: '删除',
                  'data-action': 'remove',
                  'data-value': option.value,
                  'data-worldid': option.worldid || ''
                }
              });
              toolbar.appendChild(removeBtn);
            }
          } else {
            // 显示/隐藏按钮（根据 hidden 状态切换）
            const visibilityBtn = this.ui.createElement({
              tag: 'i',
              className: ['icon', 'iconfont', option.hidden ? 'icon-hidden-annotation' : 'icon-show-annotation', 'selection-list-action-btn'],
              attributes: {
                title: option.hidden ? '显示' : '隐藏',
                'data-action': option.hidden ? 'show' : 'hide',
                'data-value': option.value
              }
            });

            // // 复制按钮 - 高斯模型目前不支持复制，隐藏按钮
            // const copyBtn = this.ui.createElement({
            //   tag: 'i',
            //   className: ['icon', 'iconfont', 'icon-CopyOutlined', 'selection-list-action-btn'],
            //   attributes: {
            //     title: '复制',
            //     'data-action': 'copy',
            //     'data-value': option.value
            //   }
            // });

            // 删除按钮
            const deleteBtn = this.ui.createElement({
              tag: 'i',
              className: ['icon', 'iconfont', 'icon-shanchu', 'selection-list-action-btn'],
              attributes: {
                title: '删除',
                'data-action': 'delete',
                'data-value': option.value
              }
            });

            toolbar.appendChild(visibilityBtn);
            // toolbar.appendChild(copyBtn);
            toolbar.appendChild(deleteBtn);
          }
          listItem.appendChild(toolbar);
        }

        // 设置初始选中状态
        if (option.selected) {
          listItem.classList.add('selected');
        }

        if (option.disabled) {
          listItem.classList.add('disabled');
        }

        if (option.hidden) {
          // listItem.classList.add('hidden');
        }

        this.listContainer.appendChild(listItem);
      });
    }
  }

  /**
   * 通知值变化
   * @param value 当前值
   * @param option 当前选项
   */
  private notifyChange(value: string | string[], option: ListItemOption | ListItemOption[]): void {
    this.onChangeCallbacks.forEach(callback => {
      try {
        callback(value, option);
      } catch (error) {
        console.error('选择列表值变化回调执行错误:', error);
      }
    });
  }

  /**
   * 通知显示操作
   * @param option 选项
   */
  private notifyShow(option: ListItemOption): void {
    this.onShowCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表显示回调执行错误:', error);
      }
    });
  }

  /**
   * 通知隐藏操作
   * @param option 选项
   */
  private notifyHide(option: ListItemOption): void {
    this.onHideCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表隐藏回调执行错误:', error);
      }
    });
  }

  /**
   * 通知复制操作
   * @param option 选项
   */
  private notifyCopy(option: ListItemOption): void {
    this.onCopyCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表复制回调执行错误:', error);
      }
    });
  }

  /**
   * 通知删除操作
   * @param option 选项
   */
  private notifyDelete(option: ListItemOption): void {
    this.onDeleteCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表删除回调执行错误:', error);
      }
    });
  }
  private notifyLoad(option: ListItemOption): void {
    this.onLoadCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表加载回调执行错误:', error);
      }
    });
  }
  private notifyRemove(option: ListItemOption): void {
    this.onRemoveCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表删除回调执行错误:', error);
      }
    });
  }
  /**
   * 通知新增模型操作
   */
  private notifyAdd(params: any): void {
    this.onAddCallbacks.forEach(callback => {
      try {
        callback(params);
      } catch (error) {
        console.error('选择列表新增回调执行错误:', error);
      }
    });
  }
  /**
   * 通知文本点击操作
   * @param option 选项
   */
  private notifyTextClick(option: ListItemOption): void {
    this.onTextClickCallbacks.forEach(callback => {
      try {
        callback(option);
      } catch (error) {
        console.error('选择列表文本点击回调执行错误:', error);
      }
    });
  }

  /**
   * 添加选项
   * @param option 要添加的选项
   */
  // addOption(option: ListItemOption): void {
  //   this.selectItemList.push(option);
  //   this.updateOptions();
  // }

  /**
   * 删除选项
   * @param index 要删除的选项索引
   */
  removeOption(index: number): void {
    if (index >= 0 && index < this.selectItemList.length) {
      this.selectItemList.splice(index, 1);
      this.updateOptions();
    }
  }

  /**
   * 根据值删除选项
   * @param value 要删除的选项值
   */
  removeOptionByValue(value: string): void {
    const index = this.selectItemList.findIndex(option => option.value === value);
    if (index !== -1) {
      this.removeOption(index);
    }
  }

  /**
   * 清空所有选项
   */
  clearOptions(): void {
    this.selectItemList = [];
    this.updateOptions();
  }

  /**
   * 设置选项列表
   * @param options 新的选项列表
   */
  setOptions(options: ListItemOption[]): void {
    this.selectItemList = [...options];
    this.updateOptions();
  }

  /**
   * 获取当前值
   * @returns 当前选中的值
   */
  getValue(): string | string[] {
    if (this.options.multiple) {
      return this.selectItemList.filter(opt => opt.selected).map(opt => opt.value);
    } else {
      const selected = this.selectItemList.find(opt => opt.selected);
      return selected ? selected.value : '';
    }
  }

  /**
   * 设置当前值
   * @param value 要设置的值
   */
  setValue(value: string | string[]): void {
    if (this.options.multiple && Array.isArray(value)) {
      // 多选模式
      this.selectItemList.forEach(opt => {
        opt.selected = value.includes(opt.value);
      });
    } else if (!this.options.multiple && typeof value === 'string') {
      // 单选模式
      this.selectItemList.forEach(opt => {
        opt.selected = opt.value === value;
      });
    }
    this.updateAllItemsState();
  }

  /**
   * 获取当前选中的选项
   * @returns 当前选中的选项
   */
  getSelectedOption(): ListItemOption | ListItemOption[] {
    if (this.options.multiple) {
      return this.selectItemList.filter(opt => opt.selected);
    } else {
      return this.selectItemList.find(opt => opt.selected) || { value: '', text: '' };
    }
  }

  /**
   * 是否禁用
   * @returns 是否禁用
   */
  isDisabled(): boolean {
    return this.options.disabled || false;
  }

  /**
   * 设置禁用状态
   * @param disabled 是否禁用
   */
  setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    if (disabled) {
      this.listContainer.setAttribute('aria-disabled', 'true');
      this.container.classList.add('selection-list-disabled');
    } else {
      this.listContainer.removeAttribute('aria-disabled');
      this.container.classList.remove('selection-list-disabled');
    }
  }

  /**
   * 设置组件激活状态（启用/禁用整个组件）
   */
  setActive(status: boolean): void {
    if (!status) {
      // 禁用整个组件
      this.container.classList.add('forbidden-event-dom');
      this.setDisabled(true);
    } else {
      // 启用整个组件
      this.container.classList.remove('forbidden-event-dom');
      this.setDisabled(false);
    }
  }

  /**
   * 添加值变化监听器
   * @param callback 回调函数
   */
  onChange(callback: (value: string | string[], option: ListItemOption | ListItemOption[]) => void): void {
    this.onChangeCallbacks.push(callback);
  }

  /**
   * 移除值变化监听器
   * @param callback 要移除的回调函数
   */
  offChange(callback: (value: string | string[], option: ListItemOption | ListItemOption[]) => void): void {
    const index = this.onChangeCallbacks.indexOf(callback);
    if (index > -1) {
      this.onChangeCallbacks.splice(index, 1);
    }
  }

  /**
   * 添加显示操作监听器
   * @param callback 回调函数
   */
  onShow(callback: (option: ListItemOption) => void): void {
    this.onShowCallbacks.push(callback);
  }

  /**
   * 移除显示操作监听器
   * @param callback 要移除的回调函数
   */
  offShow(callback: (option: ListItemOption) => void): void {
    const index = this.onShowCallbacks.indexOf(callback);
    if (index > -1) {
      this.onShowCallbacks.splice(index, 1);
    }
  }

  /**
   * 添加隐藏操作监听器
   * @param callback 回调函数
   */
  onHide(callback: (option: ListItemOption) => void): void {
    this.onHideCallbacks.push(callback);
  }

  /**
   * 移除隐藏操作监听器
   * @param callback 要移除的回调函数
   */
  offHide(callback: (option: ListItemOption) => void): void {
    const index = this.onHideCallbacks.indexOf(callback);
    if (index > -1) {
      this.onHideCallbacks.splice(index, 1);
    }
  }

  /**
   * 添加复制操作监听器
   * @param callback 回调函数
   */
  onCopy(callback: (option: ListItemOption) => void): void {
    this.onCopyCallbacks.push(callback);
  }

  /**
   * 移除复制操作监听器
   * @param callback 要移除的回调函数
   */
  offCopy(callback: (option: ListItemOption) => void): void {
    const index = this.onCopyCallbacks.indexOf(callback);
    if (index > -1) {
      this.onCopyCallbacks.splice(index, 1);
    }
  }

  /**
   * 添加删除操作监听器
   * @param callback 回调函数
   */
  onDelete(callback: (option: ListItemOption) => void): void {
    this.onDeleteCallbacks.push(callback);
  }
  /**
   * 移除删除操作监听器
   * @param callback 要移除的回调函数
   */
  offDelete(callback: (option: ListItemOption) => void): void {
    const index = this.onDeleteCallbacks.indexOf(callback);
    if (index > -1) {
      this.onDeleteCallbacks.splice(index, 1);
    }
  }

  onLoad(callback: (option: ListItemOption) => void): void {
    this.onLoadCallbacks.push(callback);
  }
  offLoad(callback: (option: ListItemOption) => void): void {
    const index = this.onLoadCallbacks.indexOf(callback);
    if (index > -1) {
      this.onLoadCallbacks.splice(index, 1);
    }
  }

  onRemove(callback: (option: ListItemOption) => void): void {
    this.onRemoveCallbacks.push(callback);
  }
  offRemove(callback: (option: ListItemOption) => void): void {
    const index = this.onRemoveCallbacks.indexOf(callback);
    if (index > -1) {
      this.onRemoveCallbacks.splice(index, 1);
    }
  }


  /**
   * 添加新增操作监听器
   * @param callback 回调函数
   */
  onAdd(callback: (params: any) => void): void {
    this.onAddCallbacks.push(callback);
  }

  /**
   * 移除新增操作监听器
   * @param callback 要移除的回调函数
   */
  offAdd(callback: (params: any) => void): void {
    const index = this.onAddCallbacks.indexOf(callback);
    if (index > -1) {
      this.onAddCallbacks.splice(index, 1);
    }
  }


  /**
   * 添加文本点击操作监听器
   * @param callback 回调函数
   */
  onTextClick(callback: (option: ListItemOption) => void): void {
    this.onTextClickCallbacks.push(callback);
  }

  /**
   * 移除文本点击操作监听器
   * @param callback 要移除的回调函数
   */
  offTextClick(callback: (option: ListItemOption) => void): void {
    const index = this.onTextClickCallbacks.indexOf(callback);
    if (index > -1) {
      this.onTextClickCallbacks.splice(index, 1);
    }
  }

  /**
   * 获取列表容器元素
   * @returns 列表容器元素
   */
  getListContainer(): HTMLElement {
    return this.listContainer;
  }

  /**
   * 获取容器元素
   * @returns 容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 获取选项列表
   * @returns 当前选项列表
   */
  getOptionsList(): ListItemOption[] {
    return [...this.selectItemList];
  }

  /**
   * 销毁选择列表组件
   */
  destroy(): void {
    this.onChangeCallbacks = [];
    this.onShowCallbacks = [];
    this.onHideCallbacks = [];
    this.onCopyCallbacks = [];
    this.onDeleteCallbacks = [];
    this.onLoadCallbacks = [];
    this.onAddCallbacks = [];
    this.onRemoveCallbacks = [];

    this.onTextClickCallbacks = [];
    this.ui.off('click', this.listContainer, () => { });

    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// 导出默认实例创建函数
export function createSelectionListUI(options: SelectionListOptions = {}): SelectionListUI {
  return new SelectionListUI(options);
}
